import { useMemo } from 'react';
import { useAuth, type UserRole } from './useAuth';

/**
 * useRole — role-aware helpers around useAuth.
 *
 * Cohesion rule 5: every permission/role check in the frontend goes
 * through this hook + the <RequireRole> wrappers. No ad-hoc role
 * comparisons elsewhere.
 *
 * The role hierarchy:
 *   CASHIER < SUPERVISOR < MANAGER < ADMIN < OWNER
 *
 * `hasMinRole('MANAGER')` returns true for MANAGER, ADMIN, OWNER.
 * `hasExactRole('CASHIER')` returns true only for CASHIER.
 */

export const ROLE_ORDER: UserRole[] = ['CASHIER', 'SUPERVISOR', 'MANAGER', 'ADMIN', 'OWNER'];

function roleRank(role: UserRole | null | undefined): number {
  if (!role) return -1;
  return ROLE_ORDER.indexOf(role);
}

export function useRole() {
  const { auth } = useAuth();
  const role = auth?.role ?? null;

  return useMemo(() => {
    const rank = roleRank(role);
    return {
      role,
      isAuthenticated: role !== null,

      /** True iff the user is exactly one of the supplied roles. */
      hasExactRole: (...allowed: UserRole[]) => role !== null && allowed.includes(role),

      /** True iff the user's role is ≥ the supplied minimum. */
      hasMinRole: (minimum: UserRole) => rank >= roleRank(minimum),

      /** Convenience shortcuts. */
      isCashier: role === 'CASHIER',
      isSupervisor: role === 'SUPERVISOR',
      isManagerPlus: rank >= roleRank('MANAGER'),
      isAdminPlus: rank >= roleRank('ADMIN'),
      isOwner: role === 'OWNER',
    };
  }, [role]);
}
